import { Button, Callout, Modal, Link } from '@dev-spendesk/grapes';
import { useState } from 'react';
import { Trans } from 'react-i18next';

import { useTranslation } from 'common/hooks/useTranslation';
import type { RequestToApprove } from 'modules/requests/api/useRequestsToApproveQuery';
import { routes, routeFor } from 'src/core/constants/routes';
import { useCompany } from 'src/core/modules/app/hooks/useCompany';
import { useGetPurchaseOrdersSettings } from 'src/core/modules/purchase-orders/detail/hooks/useGetPurchaseOrderSettings';

type Props = {
  isOpen: boolean;
  onCancel(): void;
  onProcessed(): Promise<void>;
  totalAmount: string;
  numberOfRequests: number;
  selectedRowIds: string[];
  requests: RequestToApprove[];
};

export const ApproveRequestsModal = ({
  isOpen,
  onCancel,
  onProcessed,
  totalAmount,
  numberOfRequests,
  selectedRowIds,
  requests,
}: Props) => {
  const { t } = useTranslation('global');
  const [isLoading, setIsLoading] = useState(false);
  const company = useCompany();
  const { data: purchaseOrdersSettings } = useGetPurchaseOrdersSettings();

  const hasPoRequests = selectedRowIds.some((id) => {
    const request = requests.find((r) => r.id === id);
    return request?.type === 'purchase_order';
  });

  const shouldShowCallout =
    hasPoRequests && purchaseOrdersSettings?.autoSendPoEmail;

  const handleApproveRequests = async () => {
    setIsLoading(true);
    try {
      await onProcessed();
    } catch {
      setIsLoading(false);
    }
  };

  return (
    <Modal
      actions={[
        <Button
          key="cancel"
          onClick={onCancel}
          text={t('misc.cancel')}
          variant="secondaryNeutral"
          isDisabled={isLoading}
        />,
        <Button
          key="confirm"
          text={t('requests.approveRequestsWithCount', {
            count: numberOfRequests,
          })}
          variant="primaryWarning"
          onClick={handleApproveRequests}
          isDisabled={isLoading}
          isLoading={isLoading}
        />,
      ]}
      iconName="triangle-warning"
      title={t('requests.toApproveList.approveModalTitle')}
      subtitle={t('requests.toApproveList.approveModalTotal', {
        amount: totalAmount,
      })}
      iconVariant="warning"
      isOpen={isOpen}
    >
      {shouldShowCallout && (
        <Callout
          title={t('requests.toApproveList.autoSendPoCallout.title')}
          variant="warning"
          className="mt-16"
        >
          <Trans
            i18nKey="requests.toApproveList.autoSendPoCallout.message"
            components={{
              url: (
                <Link
                  href={routeFor(routes.PURCHASE_ORDERS.path, {
                    company: company.id,
                  })}
                />
              ),
            }}
          />
        </Callout>
      )}
    </Modal>
  );
};
